import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowLeft, Trash2, Mail, Smartphone, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import logo from "@/assets/logo.png";
import ExploreMore from "@/components/seo/ExploreMore";


const SUPPORT_EMAIL = "contact@thehalaldividendcalendar.com";
const PAGE_URL = "https://getpiefolio.com/halal-dividend-calendar/delete-account";

const HalalDividendCalendarDeleteAccount = () => {
  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Delete Your Halal Dividend Calendar Account</title>
        <meta
          name="description"
          content="How to delete your Halal Dividend Calendar account and associated data, what data is removed, and how long deletion takes."
        />
        <link rel="canonical" href={PAGE_URL} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Delete Your Halal Dividend Calendar Account" />
        <meta property="og:description" content="Request deletion of your Halal Dividend Calendar account and data." />
        <meta property="og:url" content={PAGE_URL} />
        <meta name="twitter:card" content="summary" />
      </Helmet>

      <header className="sticky top-0 z-50 bg-background/80 backdrop-blur-lg border-b border-border">
        <div className="container mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <img src={logo} alt="PieFolio AI" className="h-8 w-auto" />
              <span className="font-bold text-xl">PieFolio AI</span>
            </Link>
            <Button asChild variant="ghost" size="sm">
              <Link to="/">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Home
              </Link>
            </Button>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 sm:px-6 py-12 max-w-3xl">
        <div className="flex items-center gap-3 mb-4">
          <Trash2 className="w-8 h-8 text-primary" />
          <h1 className="text-3xl sm:text-4xl font-extrabold">Delete your Halal Dividend Calendar account</h1>
        </div>
        <p className="text-muted-foreground mb-10">
          This page explains how to request deletion of your account and data for the mobile app{" "}
          <strong>Halal Dividend Calendar</strong>. Last updated: August 2026.
        </p>

        <div className="space-y-8">
          <section>
            <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
              <Smartphone className="w-5 h-5 text-primary" />
              Option 1 — Delete from inside the app
            </h2>
            <ol className="list-decimal pl-6 space-y-2 text-muted-foreground leading-relaxed">
              <li>Open the Halal Dividend Calendar app and sign in.</li>
              <li>
                Go to <strong>Settings</strong> (or <strong>Profile</strong>).
              </li>
              <li>
                Tap <strong>Account</strong>, then <strong>Delete account</strong>.
              </li>
              <li>Confirm the deletion when prompted.</li>
            </ol>
            <p className="text-muted-foreground leading-relaxed mt-4">
              Your account and its data are removed immediately after confirmation, and you are signed out.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
              <Mail className="w-5 h-5 text-primary" />
              Option 2 — Request deletion by email
            </h2>
            <p className="text-muted-foreground leading-relaxed">
              If you can no longer access the app, email us from the address you used to register with the subject line{" "}
              <strong>“Delete my account”</strong>:
            </p>
            <p className="mt-4">
              <a
                className="font-semibold text-primary underline break-all"
                href={`mailto:${SUPPORT_EMAIL}?subject=Delete%20my%20account`}
              >
                {SUPPORT_EMAIL}
              </a>
            </p>
            <p className="text-muted-foreground leading-relaxed mt-4">
              We verify the request comes from the account owner and then delete the account.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4">What gets deleted</h2>
            <ul className="list-disc pl-6 space-y-2 text-muted-foreground leading-relaxed">
              <li>Your account and authentication identifiers (email, user ID).</li>
              <li>Your watchlists, holdings and any dividend entries you added.</li>
              <li>Your in-app preferences and notification settings.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
              <Clock className="w-5 h-5 text-primary" />
              Timing and retention
            </h2>
            <ul className="list-disc pl-6 space-y-2 text-muted-foreground leading-relaxed">
              <li>
                In-app deletions take effect immediately. Email requests are completed within 30 days of verification.
              </li>
              <li>Encrypted backups may retain data for up to 30 additional days before being overwritten.</li>
              <li>
                Records we must keep for legal, tax or fraud-prevention reasons are retained only for as long as the law
                requires.
              </li>
            </ul>
          </section>

          <section className="p-6 rounded-lg bg-muted/50 border border-border">
            <h2 className="text-xl font-bold mb-2">Questions</h2>
            <p className="text-muted-foreground leading-relaxed">
              Contact{" "}
              <a className="text-primary underline break-all" href={`mailto:${SUPPORT_EMAIL}`}>
                {SUPPORT_EMAIL}
              </a>{" "}
              for anything related to your account or data.
            </p>
          </section>
        </div>
        <ExploreMore />
      </main>

    </div>
  );
};

export default HalalDividendCalendarDeleteAccount;
